import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, Layers, Plus, Printer } from 'lucide-react'

import type { ListPurchaseLotsParams } from '@/lib/api/purchase-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    LOT_CARD_CODE_CLASS,
    LotListCard,
} from '@/components/common/LotListCard'
import { RefreshButton } from '@/components/common/refresh-button'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { usePrintPurchaseLotLabel } from '@/hooks/use-lot-label-print'
import {
    usePurchaseLots,
    usePurchaseLotsInfinite,
} from '@/hooks/use-purchase-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Search {
    search?: string
    only_available?: '1'
    page?: number
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/lots/',
)({
    component: Page,
    validateSearch: (search: Record<string, unknown>): Search => {
        const out: Search = {}
        if (typeof search.search === 'string' && search.search) {
            out.search = search.search
        }
        if (search.only_available === '1') out.only_available = '1'
        const page =
            typeof search.page === 'number' ? search.page : Number(search.page)
        if (Number.isFinite(page) && page > 1) out.page = page
        return out
    },
})

function Page() {
    const search = Route.useSearch()
    const navigate = useNavigate({ from: '/purchase-lots/lots/' })
    const setSearch = (next: Search) =>
        void navigate({ search: () => next, replace: false })
    const params = useMemo<ListPurchaseLotsParams>(
        () => ({
            page: search.page ?? 1,
            per_page: 50,
            search: search.search,
            only_available: search.only_available,
        }),
        [search],
    )
    const isMobile = useIsMobile()
    const { data, isLoading, isFetching, refetch } = usePurchaseLots(params, {
        enabled: !isMobile,
    })

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({
            per_page: 50,
            ...(search.search ? { search: search.search } : {}),
            ...(search.only_available
                ? { only_available: search.only_available }
                : {}),
        }),
        [search.search, search.only_available],
    )
    const infinite = usePurchaseLotsInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileItems = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const printMut = usePrintPurchaseLotLabel()
    const rows = data?.data ?? []
    const filterActive =
        !!search.search || !!search.only_available || (search.page ?? 1) > 1
    const clearFilter = () => setSearch({})

    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    const renderFilters = () => (
        <>
            <div>
                <Label className="mb-1 block">{m.pl_lots_search_label()}</Label>
                <Input
                    placeholder={m.pl_lots_search_placeholder()}
                    defaultValue={search.search ?? ''}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter') return
                        const value = (event.target as HTMLInputElement).value
                        setSearch({
                            ...search,
                            search: value || undefined,
                            page: undefined,
                        })
                    }}
                />
            </div>
            <div className="flex items-end">
                <Button
                    type="button"
                    variant={
                        search.only_available === '1' ? 'default' : 'outline'
                    }
                    className="w-full md:w-auto"
                    onClick={() =>
                        setSearch({
                            ...search,
                            only_available:
                                search.only_available === '1' ? undefined : '1',
                            page: undefined,
                        })
                    }
                >
                    {m.pl_lots_only_available()}
                </Button>
            </div>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<Layers className="size-5 shrink-0" />}
                title={m.pl_lots_title()}
                utilityActions={
                    <>
                        <RefreshButton
                            onClick={() => void refetch()}
                            loading={isFetching}
                        />
                        <ClearFilterButton
                            active={filterActive}
                            onClear={clearFilter}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setFilterDialogOpen(true)}
                        >
                            <Filter className="size-4" />
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/purchase-lots/stock-in-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pl_lots_create_pnk()}
                        </Link>
                    </Button>
                }
            />

            <div className="hidden md:grid gap-3 md:grid-cols-[1fr_auto]">
                {renderFilters()}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.pl_lots_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1"
                            onClick={() => {
                                clearFilter()
                                setFilterDialogOpen(false)
                            }}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.pl_lots_loading()}
                    </div>
                ) : mobileItems.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.pl_lots_empty()}
                    </div>
                ) : (
                    <>
                        {mobileItems.map((lot) => (
                            <LotListCard
                                key={lot.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/purchase-lots/lots/$id',
                                        params: { id: lot.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/purchase-lots/lots/$id"
                                        params={{ id: lot.id }}
                                        className={LOT_CARD_CODE_CLASS}
                                    >
                                        {lot.code}
                                    </Link>
                                }
                                attribute={lot.grower_name ?? '—'}
                                attributeSub={lot.purchase_code ?? undefined}
                                quantityAvailable={lot.quantity_available}
                                quantityUnit="kg"
                                createdAt={lot.created_at}
                                actions={
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        aria-label={m.lot_label_print_btn_title()}
                                        title={m.lot_label_print_btn_title()}
                                        disabled={printMut.isPending}
                                        onClick={() => printMut.mutate(lot.id)}
                                    >
                                        <Printer className="size-4" />
                                    </Button>
                                }
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.pl_lots_col_code()}</TableHead>
                            <TableHead>{m.pl_lots_col_purchase()}</TableHead>
                            <TableHead>{m.pl_lots_col_grower()}</TableHead>
                            <TableHead className="text-right">
                                {m.pl_lots_col_available()}
                            </TableHead>
                            <TableHead>{m.pl_lots_col_created_at()}</TableHead>
                            <TableHead className="text-right" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.pl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.pl_lots_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((lot) => (
                                <TableRow
                                    key={lot.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/purchase-lots/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/purchase-lots/lots/$id"
                                            params={{ id: lot.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {lot.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                        {lot.purchase_code ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {lot.grower_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(lot.quantity_available)} kg
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(lot.created_at)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            title={m.lot_label_print_btn_title()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
